import { Link } from 'react-router-dom';
import { ArrowRight, Volume2, MailQuestion, CalendarCheck, NotebookPen, Compass, Mail, Gift } from 'lucide-react';
import { Reveal } from '@/components/Reveal';
import { MagneticButton } from '@/components/MagneticButton';
import { AnimatedIconBadge } from '@/components/howItWorks/AnimatedIconBadge';
import { HOW_IT_WORKS } from '@/constants/testIds';
import { STAGES, STAGE_CONTENT, STAGE_ICON_NAMES, TEASER_STAGE_SLUGS } from '@/lib/howItWorksContent';

const ICONS = { MailQuestion, CalendarCheck, NotebookPen, Compass, Mail, Gift };

// Compact 2-track preview (3 stages only) used on the homepage to tease the
// full /how-it-works experience. Not the full timeline — purely a hook.
export const HowItWorksTeaser = () => {
  const previewStages = STAGES.filter((s) => TEASER_STAGE_SLUGS.includes(s.slug));

  return (
    <section
      className="bg-[#0A0A0A] border-y border-[hsl(var(--lxra-border))] lxra-grain"
      data-testid={HOW_IT_WORKS.teaserSection}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 py-14 sm:py-18 grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-10 items-center">
        <Reveal className="lg:col-span-5">
          <p className="text-xs tracking-[0.22em] uppercase text-[hsl(var(--lxra-gold))] mb-3">How It Works</p>
          <h2 className="font-display text-3xl sm:text-4xl tracking-[0.03em] leading-[1.02] text-[hsl(var(--lxra-fg))]">
            AI WORKFLOW, CINEMATIC RESULTS.
          </h2>
          <p className="mt-4 max-w-md text-sm sm:text-base text-[hsl(var(--lxra-muted))] leading-relaxed">
            See how LXRA pairs human craft with AI tools across booking, planning, shoot day, and delivery.
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <MagneticButton>
              <Link
                to="/how-it-works"
                data-testid={HOW_IT_WORKS.teaserPrimaryCta}
                className="inline-flex items-center h-11 px-6 rounded-xl bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] transition-colors duration-200 text-xs uppercase tracking-[0.1em] font-semibold"
              >
                Explore the Workflow
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </MagneticButton>
            <Link
              to="/how-it-works#voice-guide"
              data-testid={HOW_IT_WORKS.teaserVoiceCta}
              className="inline-flex items-center h-11 px-5 rounded-xl border border-[hsl(var(--lxra-gold))]/50 text-[hsl(var(--lxra-fg))] hover:border-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold))] transition-colors duration-200 text-xs uppercase tracking-[0.1em] font-medium"
            >
              <Volume2 className="mr-2 h-4 w-4" />
              Play the Voice Guide
            </Link>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-7">
          <Link to="/how-it-works#timeline" data-testid={HOW_IT_WORKS.teaserMiniPreview} className="block group">
            <div className="relative rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-5 sm:p-7">
              <div className="grid grid-cols-2 gap-3 sm:gap-4 text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--lxra-muted))] mb-3">
                <span>Traditional</span>
                <span className="text-right">LXRA AI-Powered</span>
              </div>
              <div className="space-y-3">
                {previewStages.map((stage, i) => {
                  const content = STAGE_CONTENT[stage.slug].client;
                  const icons = STAGE_ICON_NAMES[stage.slug];
                  return (
                    <div key={stage.slug} className="grid grid-cols-2 gap-3 sm:gap-4">
                      <div className="flex items-start gap-2.5 rounded-lg border border-[hsl(var(--lxra-border))] bg-card p-3 transition-transform duration-200 group-hover:-translate-y-0.5">
                        <AnimatedIconBadge icon={ICONS[icons.traditional]} variant="traditional" size={28} />
                        <div className="min-w-0">
                          <p className="text-[10px] text-[hsl(var(--lxra-muted))] mb-1">{String(i + 1).padStart(2, '0')} · {stage.title}</p>
                          <p className="text-xs text-[hsl(var(--lxra-fg))] line-clamp-2">{content.traditional.title}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2.5 rounded-lg border border-[hsl(var(--lxra-gold)/0.35)] bg-card p-3 transition-transform duration-200 group-hover:-translate-y-0.5">
                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] text-[hsl(var(--lxra-muted))] mb-1 text-right">{stage.title} · {String(i + 1).padStart(2, '0')}</p>
                          <p className="text-xs text-[hsl(var(--lxra-fg))] text-right line-clamp-2">{content.lxra.title}</p>
                        </div>
                        <AnimatedIconBadge icon={ICONS[icons.lxra]} variant="lxra" size={28} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Link>
        </Reveal>
      </div>
    </section>
  );
};
